import { useState, useEffect, useCallback } from 'react';
import { vendorPaymentAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Send, Trash2, Pencil, Check, X, TrendingUp, ChevronDown, ChevronRight, Banknote, Undo2, FileText, Download, Paperclip } from 'lucide-react';
import { FlagCard } from './FlagCard';
import { LineRow } from './LineRow';
import { ApprovalChain } from './ApprovalChain';

const STATUS_BADGE = {
  DRAFT: 'bg-slate-100 text-slate-700', CHECKED: 'bg-sky-100 text-sky-800',
  SUBMITTED: 'bg-amber-100 text-amber-800', PENDING_APPROVAL: 'bg-amber-100 text-amber-800',
  CHANGES_REQUESTED: 'bg-orange-100 text-orange-800',
  APPROVED: 'bg-emerald-100 text-emerald-800', APPROVED_FOR_PAYMENT: 'bg-emerald-100 text-emerald-800',
  PAID: 'bg-indigo-100 text-indigo-800',
  REJECTED: 'bg-red-100 text-red-800', CANCELLED: 'bg-slate-200 text-slate-600',
};

function PriceMatrixLine({ line }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-sm border border-border" data-testid={`vp-price-matrix-line-${line.line_id}`}>
      <div className="flex items-center gap-3 p-2 text-sm">
        <button onClick={() => setOpen(v => !v)} disabled={!line.last_5_purchases?.length} className="shrink-0">
          {line.last_5_purchases?.length ? (open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />) : <span className="w-3.5 inline-block" />}
        </button>
        <span className="font-mono font-medium w-28 truncate" data-testid="vp-pm-line-sku">{line.sku_label}</span>
        <span className="text-muted-foreground w-14 text-right">{line.pi_qty}</span>
        <span className="w-20 text-right">{line.pi_unit_price_usd != null ? `$${line.pi_unit_price_usd.toFixed(2)}` : '—'}</span>
        <span className="w-28 text-right text-muted-foreground">
          {line.weighted_avg_usd != null ? `$${line.weighted_avg_usd.toFixed(2)}` : 'no history'}
          {line.diff_vs_weighted_avg_pct != null && (
            <span className={line.diff_vs_weighted_avg_pct > 0 ? 'text-red-600 ml-1' : 'text-emerald-600 ml-1'}>
              ({line.diff_vs_weighted_avg_pct > 0 ? '+' : ''}{line.diff_vs_weighted_avg_pct}%)
            </span>
          )}
        </span>
        <span className="w-24 text-right">{line.last_sale ? `$${line.last_sale.unit_price_usd.toFixed(2)}` : 'no sale'}</span>
        <span className={`w-20 text-right font-medium ${line.margin_pct == null ? 'text-muted-foreground' : line.margin_pct < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
          {line.margin_pct != null ? `${line.margin_pct}%` : '—'}
        </span>
      </div>
      {open && (
        <div className="border-t border-border bg-muted/30 p-2 text-xs space-y-1" data-testid="vp-pm-last5">
          {line.last_5_purchases.map((p, i) => (
            <div key={i} className="flex justify-between text-muted-foreground">
              <span>{p.date} — {p.po_number} ({p.vendor_name})</span>
              <span>{p.quantity} × ${p.unit_price_usd.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PriceMatrixPanel({ requestId }) {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    vendorPaymentAPI.getPriceMatrix(requestId)
      .then((res) => { if (mounted) setMatrix(res.data); })
      .catch((err) => {
        if (!mounted) return;
        setMatrix(null);
        if (err.response?.status !== 403) {
          toast.error('Could not load the price matrix — please retry');
        }
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [requestId]);

  if (loading) return <div className="rounded-sm border border-border p-3 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>;
  if (!matrix) return null;

  return (
    <div className="rounded-sm border border-indigo-200 bg-indigo-50/40 p-3" data-testid="vp-price-matrix-panel">
      <p className="text-xs font-semibold uppercase text-indigo-900 mb-2 flex items-center gap-1.5">
        <TrendingUp className="h-3.5 w-3.5" />Price Matrix {matrix.live ? '(live)' : '(frozen at approval)'} — visible to you only
      </p>
      <div className="flex items-center gap-3 text-[10px] uppercase text-muted-foreground px-2 pl-7 mb-1">
        <span className="w-28">SKU</span><span className="w-14 text-right">Qty</span>
        <span className="w-20 text-right">PI Price</span><span className="w-28 text-right">5-Buy Avg</span>
        <span className="w-24 text-right">Last Sale</span><span className="w-20 text-right">Margin</span>
      </div>
      <div className="space-y-1">
        {matrix.lines.map((ln) => <PriceMatrixLine key={ln.line_id} line={ln} />)}
      </div>
      <div className="flex justify-between text-sm font-medium mt-2 pt-2 border-t border-indigo-200" data-testid="vp-pm-summary">
        <span>PI total: ${matrix.summary.pi_total_usd.toFixed(2)}</span>
        <span>{matrix.summary.last_sale_valued_total_usd != null ? `At last sale: $${matrix.summary.last_sale_valued_total_usd.toFixed(2)}` : ''}</span>
        <span className={matrix.summary.overall_margin_pct == null ? '' : matrix.summary.overall_margin_pct < 0 ? 'text-red-600' : 'text-emerald-600'}>
          {matrix.summary.overall_margin_pct != null ? `Overall margin: ${matrix.summary.overall_margin_pct}%` : 'No sale history'}
        </span>
      </div>
    </div>
  );
}

function _todayLocalISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function MarkPaidForm({ requestId, onDone }) {
  const [paidDate, setPaidDate] = useState(_todayLocalISO());
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!paidDate || !reference.trim()) return;
    setBusy(true);
    try {
      await vendorPaymentAPI.markPaid(requestId, { paid_date: paidDate, payment_reference: reference.trim(), payment_note: note.trim() || null });
      toast.success('Marked as Paid');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not mark as paid');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-sm border border-border p-3 space-y-2" data-testid="vp-mark-paid-form">
      <p className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1.5"><Banknote className="h-3.5 w-3.5" />Mark as Paid</p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] text-muted-foreground uppercase">Paid Date</Label>
          <Input type="date" value={paidDate} max={_todayLocalISO()} onChange={(e) => setPaidDate(e.target.value)} className="h-8 text-sm" data-testid="vp-mark-paid-date" />
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground uppercase">Payment Reference</Label>
          <Input value={reference} onChange={(e) => setReference(e.target.value)} className="h-8 text-sm" data-testid="vp-mark-paid-reference" />
        </div>
      </div>
      <div>
        <Label className="text-[11px] text-muted-foreground uppercase">Note (optional)</Label>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="text-sm" data-testid="vp-mark-paid-note" />
      </div>
      <Button size="sm" className="rounded-sm" disabled={busy || !paidDate || !reference.trim()} onClick={submit} data-testid="vp-mark-paid-submit-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Banknote className="h-4 w-4 mr-1.5" />}Confirm Paid
      </Button>
    </div>
  );
}


function EditableField({ label, value, original, onSave, type = 'text', testid }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const wasCorrected = original !== undefined && original !== null && String(original) !== String(value ?? '');

  const save = async () => {
    setSaving(true);
    try { await onSave(type === 'number' ? (val === '' ? null : parseFloat(val)) : (val || null)); setEditing(false); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <Label className="text-[11px] text-muted-foreground uppercase">{label}</Label>
      {editing ? (
        <div className="flex items-center gap-1 mt-0.5">
          <Input value={val} type={type} onChange={(e) => setVal(e.target.value)} className="h-7 text-sm" data-testid={`${testid}-input`} />
          <Button size="icon" className="h-7 w-7 rounded-sm shrink-0" disabled={saving} onClick={save} data-testid={`${testid}-save`}><Check className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="outline" className="h-7 w-7 rounded-sm shrink-0" onClick={() => { setVal(value ?? ''); setEditing(false); }}><X className="h-3.5 w-3.5" /></Button>
        </div>
      ) : (
        <div className="group">
          <div className="flex items-center gap-1 mt-0.5">
            <span className="text-sm" data-testid={testid}>{value ?? <span className="text-red-500 italic">null</span>}</span>
            <button onClick={() => { setVal(value ?? ''); setEditing(true); }} className="opacity-0 group-hover:opacity-100" data-testid={`${testid}-edit-btn`}>
              <Pencil className="h-3 w-3 text-muted-foreground" />
            </button>
          </div>
          {wasCorrected && (
            <p className="text-[11px] text-amber-600 italic" data-testid={`${testid}-original`}>originally extracted: {original ?? 'null'}</p>
          )}
        </div>
      )}
    </div>
  );
}

function DocumentsCard({ requestId, req, readOnly, isCreator, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [busyFileId, setBusyFileId] = useState(null);
  const files = req.supporting_files || [];
  const canManage = isCreator && !readOnly;

  const download = async (kind, fileId, name) => {
    try {
      const res = kind === 'pi'
        ? await vendorPaymentAPI.getFile(requestId)
        : await vendorPaymentAPI.getSupportingFile(requestId, fileId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download file');
    }
  };

  const handleAdd = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    try {
      await vendorPaymentAPI.addSupportingFiles(requestId, picked);
      toast.success('Supporting file(s) attached');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to attach file(s)');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemove = async (fileId) => {
    if (!window.confirm('Remove this supporting file? This cannot be undone.')) return;
    setBusyFileId(fileId);
    try {
      await vendorPaymentAPI.removeSupportingFile(requestId, fileId);
      toast.success('Supporting file removed');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove file');
    } finally {
      setBusyFileId(null);
    }
  };

  return (
    <div className="rounded-sm border border-border p-3" data-testid="vp-documents-card">
      <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Documents</p>
      <button className="w-full flex items-center justify-between text-sm py-1 hover:underline text-left" onClick={() => download('pi', null, req.pi_file_name)} data-testid="vp-pi-download-link" title={`Download ${req.pi_file_name}`} aria-label={`Download PI file ${req.pi_file_name}`}>
        <span className="flex items-center gap-1.5 truncate"><FileText className="h-3.5 w-3.5 shrink-0" />{req.pi_file_name}</span>
        <Download className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </button>
      {files.length > 0 && (
        <div className="mt-2 space-y-1">
          <p className="text-[11px] text-muted-foreground uppercase">Supporting files (reference only)</p>
          {files.map(f => (
            <div key={f.file_id} className="flex items-center justify-between gap-2 text-sm" data-testid={`vp-supporting-file-${f.file_id}`}>
              <button className="flex items-center gap-1.5 truncate hover:underline text-left" onClick={() => download('supporting', f.file_id, f.name)} data-testid={`vp-supporting-file-download-${f.file_id}`} title={`Download ${f.name}`} aria-label={`Download supporting file ${f.name}`}>
                <Paperclip className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{f.name}</span>
              </button>
              {canManage && (
                <button onClick={() => handleRemove(f.file_id)} disabled={busyFileId === f.file_id} className="shrink-0" data-testid={`vp-supporting-file-remove-${f.file_id}`} title={`Remove ${f.name}`} aria-label={`Remove supporting file ${f.name}`}>
                  {busyFileId === f.file_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 text-red-500" />}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {canManage && files.length < 5 && (
        <div className="mt-2">
          <label className="inline-flex items-center gap-1.5 text-xs text-primary cursor-pointer hover:underline">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
            Attach supporting file
            <input type="file" multiple accept=".xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg" className="hidden" onChange={handleAdd} disabled={uploading} data-testid="vp-supporting-file-add-input" />
          </label>
        </div>
      )}
    </div>
  );
}

export function RequestDetailModal({ requestId, open, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const { user, isAdmin, isSuperAdmin } = useAuth();

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      const res = await vendorPaymentAPI.get(requestId);
      if (res.data.request.status === 'DRAFT') {
        const checkRes = await vendorPaymentAPI.check(requestId);
        setData(checkRes.data);
      } else {
        setData(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load request');
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const req = data?.request;
  const lines = data?.lines || [];
  const readOnly = req && !['DRAFT', 'CHECKED', 'CHANGES_REQUESTED'].includes(req.status);

  const saveHeaderField = async (field, value) => {
    await vendorPaymentAPI.updateHeader(requestId, { [field]: value });
    await load();
  };

  const saveLine = async (lineId, patch) => {
    await vendorPaymentAPI.updateLine(requestId, lineId, patch);
    await load();
  };

  const handleRecheck = async () => {
    setBusy(true);
    try {
      // Use /check (not /recheck) — it's the only endpoint that transitions
      // CHANGES_REQUESTED -> CHECKED (required before Submit becomes available again
      // after a send-back). Re-extraction is skipped automatically since extracted_raw
      // already exists, so this behaves identically to /recheck plus the status flip.
      await vendorPaymentAPI.check(requestId);
      toast.success('Re-checked');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recheck failed');
    } finally {
      setBusy(false);
    }
  };

  const handleJustify = async (flagId, justification) => {
    try {
      await vendorPaymentAPI.justifyFlag(requestId, flagId, justification);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save justification');
    }
  };

  const handleSubmit = async () => {
    setBusy(true);
    try {
      await vendorPaymentAPI.submit(requestId);
      toast.success('Submitted for approval');
      onChanged?.();
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Could not submit');
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = async (comment) => {
    try {
      await vendorPaymentAPI.approve(requestId, 'APPROVED', comment);
      toast.success('Approved');
      onChanged?.();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not approve');
    }
  };

  const handleReject = async (comment) => {
    try {
      await vendorPaymentAPI.approve(requestId, 'REJECTED', comment);
      toast.success('Request rejected');
      onChanged?.();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reject');
    }
  };

  const handleSendBack = async (comment) => {
    try {
      await vendorPaymentAPI.approve(requestId, 'SEND_BACK', comment);
      toast.success('Sent back to the uploader for changes');
      onChanged?.();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send back');
    }
  };

  const handleReassign = async (newApproverUserId, reason) => {
    try {
      await vendorPaymentAPI.reassign(requestId, newApproverUserId, reason);
      toast.success('Approval step reassigned');
      onChanged?.();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reassign');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this draft request?')) return;
    setBusy(true);
    try {
      await vendorPaymentAPI.delete(requestId);
      toast.success('Draft deleted');
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    const reason = window.prompt('Why are you cancelling this request? (required)');
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try {
      await vendorPaymentAPI.cancel(requestId, reason.trim());
      toast.success('Request cancelled');
      onChanged?.();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel');
    } finally {
      setBusy(false);
    }
  };

  const handleUndoPaid = async () => {
    const reason = window.prompt('Why are you undoing the Paid status? (required)');
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try {
      await vendorPaymentAPI.undoPaid(requestId, reason.trim());
      toast.success('Paid status undone');
      onChanged?.();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not undo Paid');
    } finally {
      setBusy(false);
    }
  };

  const hardBlocks = (req?.flags || []).filter(f => f.severity === 'HARD_BLOCK');
  const unjustifiedBlocking = (req?.flags || []).filter(f => f.severity === 'BLOCKING' && !f.justification);
  const canSubmit = req?.status === 'CHECKED' && hardBlocks.length === 0 && unjustifiedBlocking.length === 0;
  const cancellableStatuses = ['SUBMITTED', 'CHANGES_REQUESTED', 'REJECTED', 'APPROVED_FOR_PAYMENT'];
  const canCancel = req && cancellableStatuses.includes(req.status) && (
    req.status === 'APPROVED_FOR_PAYMENT' ? isSuperAdmin : (isAdmin || req.created_by === user?.id)
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="vp-request-detail-modal">
        {loading || !req ? (
          <>
            <DialogHeader><DialogTitle className="sr-only">Loading vendor payment request</DialogTitle></DialogHeader>
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 flex-wrap">
                <DialogTitle className="font-mono">{req.po_number}</DialogTitle>
                <Badge className={`text-[10px] rounded-sm ${STATUS_BADGE[req.status] || ''}`} data-testid="vp-request-status-badge">{req.status}</Badge>
              </div>
            </DialogHeader>

            <div className="space-y-5">
              {req.status === 'CHANGES_REQUESTED' && (() => {
                const sentBackStep = [...(data?.steps || [])].reverse().find(s => s.status === 'SENT_BACK');
                return (
                  <div className="rounded-sm border border-orange-300 bg-orange-50 p-3 text-sm text-orange-900" data-testid="vp-sent-back-banner">
                    <p className="font-semibold">Sent back for changes{sentBackStep ? ` by ${sentBackStep.decided_by_name}` : ''}</p>
                    {sentBackStep?.comment && <p className="mt-1">{sentBackStep.comment}</p>}
                    <p className="mt-1 text-xs text-orange-700">Make your corrections below, then run Check again and resubmit — the approval chain will restart at level 1.</p>
                  </div>
                );
              })()}

              {req.status === 'CANCELLED' && (
                <div className="rounded-sm border border-slate-300 bg-slate-100 p-3 text-sm text-slate-800" data-testid="vp-cancelled-banner">
                  <p className="font-semibold">Cancelled{req.cancelled_by_name ? ` by ${req.cancelled_by_name}` : ''}{req.cancelled_at ? ` on ${new Date(req.cancelled_at).toLocaleString()}` : ''}</p>
                  {req.cancel_reason && <p className="mt-1">Reason: {req.cancel_reason}</p>}
                </div>
              )}

              {req.status === 'PAID' && (
                <div className="rounded-sm border border-indigo-300 bg-indigo-50 p-3 text-sm text-indigo-900" data-testid="vp-paid-banner">
                  <p className="font-semibold">Paid on {req.paid_date} — reference {req.payment_reference}</p>
                  {req.marked_paid_by_name && <p className="mt-1 text-xs text-indigo-700">Marked by {req.marked_paid_by_name}</p>}
                  {req.payment_note && <p className="mt-1">{req.payment_note}</p>}
                </div>
              )}

              {req.can_view_price_matrix && <PriceMatrixPanel requestId={requestId} />}

              <div className="rounded-sm border border-border p-3 bg-muted/30">
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Purchase Order (reference, read-only)</p>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div><Label className="text-[11px] text-muted-foreground uppercase">Vendor</Label><p>{req.po_vendor_name}</p></div>
                  <div><Label className="text-[11px] text-muted-foreground uppercase">Total</Label><p>{req.po_currency_code} {req.po_total}</p></div>
                  <div><Label className="text-[11px] text-muted-foreground uppercase">Status</Label><p>{req.po_status}</p></div>
                </div>
              </div>

              <DocumentsCard requestId={requestId} req={req} readOnly={readOnly} isCreator={req.created_by === user?.id} onChanged={load} />

              <div className="rounded-sm border border-border p-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">PI Document — extracted via {req.extraction_method || '—'} {req.pi_total_amount_derived ? '(total derived from lines)' : ''}</p>
                <div className="grid grid-cols-3 gap-3">
                  <EditableField label="Vendor Name" value={req.vendor_name} original={req.extracted_raw?.vendor_name} onSave={(v) => saveHeaderField('vendor_name', v)} testid="field-vendor-name" />
                  <EditableField label="PI Number" value={req.pi_number} original={req.extracted_raw?.pi_number} onSave={(v) => saveHeaderField('pi_number', v)} testid="field-pi-number" />
                  <EditableField label="PI Date" value={req.pi_date} original={req.extracted_raw?.pi_date} onSave={(v) => saveHeaderField('pi_date', v)} testid="field-pi-date" />
                  <EditableField label="Currency" value={req.currency_code} original={req.extracted_raw?.currency} onSave={(v) => saveHeaderField('currency_code', v)} testid="field-currency" />
                  <EditableField label="PI Total Amount" value={req.pi_total_amount} original={req.extracted_raw?.pi_total_amount} type="number" onSave={(v) => saveHeaderField('pi_total_amount', v)} testid="field-pi-total" />
                  <EditableField label="Amount Requested" value={req.amount_requested} type="number" onSave={(v) => saveHeaderField('amount_requested', v)} testid="field-amount-requested" />
                </div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mt-3 mb-2">Bank Details (extracted exactly as written)</p>
                <div className="grid grid-cols-2 gap-3">
                  <EditableField label="Beneficiary" value={req.bank_details?.beneficiary} original={req.extracted_raw?.bank_details?.beneficiary} onSave={(v) => saveHeaderField('bank_details', { ...req.bank_details, beneficiary: v })} testid="field-beneficiary" />
                  <EditableField label="Bank Name" value={req.bank_details?.bank_name} original={req.extracted_raw?.bank_details?.bank_name} onSave={(v) => saveHeaderField('bank_details', { ...req.bank_details, bank_name: v })} testid="field-bank-name" />
                  <EditableField label="Account Number" value={req.bank_details?.account_number} original={req.extracted_raw?.bank_details?.account_number} onSave={(v) => saveHeaderField('bank_details', { ...req.bank_details, account_number: v })} testid="field-account-number" />
                  <EditableField label="SWIFT" value={req.bank_details?.swift} original={req.extracted_raw?.bank_details?.swift} onSave={(v) => saveHeaderField('bank_details', { ...req.bank_details, swift: v })} testid="field-swift" />
                </div>
              </div>

              {lines.length > 0 && (
                <div className="rounded-sm border border-border overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr className="text-left text-[11px] uppercase text-muted-foreground">
                        <th className="p-2">Description</th><th className="p-2">Part #</th>
                        <th className="p-2 text-right">Qty</th><th className="p-2 text-right">Unit Price</th><th className="p-2 text-right">Line Total</th>
                        <th className="p-2">PO Line</th><th className="p-2">Match</th><th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((ln) => <LineRow key={ln.id} line={ln} onSave={saveLine} readOnly={readOnly} />)}
                    </tbody>
                  </table>
                </div>
              )}

              {(req.flags || []).length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Validation Flags</p>
                  {req.flags.map((fl) => <FlagCard key={fl.id} flag={fl} onJustify={handleJustify} disabled={readOnly} />)}
                </div>
              )}
              {(req.flags || []).length === 0 && req.status !== 'DRAFT' && (
                <p className="text-sm text-emerald-700">No validation flags — clean match against the PO.</p>
              )}

              <ApprovalChain request={req} steps={data?.steps} currentUserId={user?.id} onApprove={handleApprove} onReject={handleReject} onSendBack={handleSendBack} onReassign={handleReassign} />

              {req.can_mark_paid && req.status === 'APPROVED_FOR_PAYMENT' && (
                <MarkPaidForm requestId={requestId} onDone={() => { onChanged?.(); load(); }} />
              )}
            </div>

            <DialogFooter className="flex-wrap gap-2">
              {req.status === 'DRAFT' && (
                <Button variant="outline" className="rounded-sm text-red-600" onClick={handleDelete} disabled={busy} data-testid="vp-delete-draft-btn">
                  <Trash2 className="h-4 w-4 mr-1.5" />Delete Draft
                </Button>
              )}
              {['CHECKED', 'CHANGES_REQUESTED'].includes(req.status) && (
                <Button variant="outline" className="rounded-sm" onClick={handleRecheck} disabled={busy} data-testid="vp-recheck-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}Run Check
                </Button>
              )}
              {req.status === 'CHECKED' && (
                <Button className="rounded-sm" onClick={handleSubmit} disabled={busy || !canSubmit} data-testid="vp-submit-btn">
                  <Send className="h-4 w-4 mr-1.5" />Submit for Approval
                </Button>
              )}
              {req.status === 'CHECKED' && !canSubmit && (
                <p className="text-xs text-muted-foreground w-full">
                  {hardBlocks.length > 0 ? 'Cannot submit — hard block present.' : 'Justify all blocking flags before submitting.'}
                </p>
              )}
              {canCancel && (
                <Button variant="outline" className="rounded-sm text-red-600" onClick={handleCancel} disabled={busy} data-testid="vp-cancel-request-btn">
                  <X className="h-4 w-4 mr-1.5" />Cancel Request
                </Button>
              )}
              {req.status === 'PAID' && isSuperAdmin && (
                <Button variant="outline" className="rounded-sm text-orange-600" onClick={handleUndoPaid} disabled={busy} data-testid="vp-undo-paid-btn">
                  <Undo2 className="h-4 w-4 mr-1.5" />Undo Paid
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
